import { Injectable, computed, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/**
 * Idiomas soportados por la aplicación.
 */
export type SupportedLanguage = 'gl' | 'es';

interface LanguageOption {
  code: SupportedLanguage;
  label: string;
  flag: string;
}

/**
 * Servicio responsable de la gestión del idioma actual.
 * - Expone el idioma activo como señal reactiva.
 * - Persiste la preferencia en localStorage.
 * - Delega en ngx-translate para el cambio de idioma en tiempo real.
 */
@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly translateService = inject(TranslateService);
  private readonly storageKey = 'portal-empleo-language';

  readonly availableLanguages: LanguageOption[] = [
    { code: 'gl', label: 'GAL', flag: 'flag-gal.png' },
    { code: 'es', label: 'ESP', flag: 'flag-esp.png' }
  ];

  readonly currentLang = signal<SupportedLanguage>(this.resolveInitialLang());
  readonly currentLanguage = computed(() =>
    this.availableLanguages.find(lang => lang.code === this.currentLang()) ?? this.availableLanguages[0]
  );

  /**
   * Inicializa el idioma a partir de la preferencia almacenada o el idioma por defecto.
   * Debe invocarse durante el APP_INITIALIZER de la aplicación.
   */
  init(): void {
    const stored = this.readStoredLang();
    if (stored && this.isSupported(stored)) {
      this.setLang(stored);
      return;
    }
    this.translateService.use(this.currentLang());
  }

  /**
   * Cambia el idioma activo y persiste la preferencia.
   */
  setLang(lang: SupportedLanguage): void {
    this.translateService.use(lang);
    this.currentLang.set(lang);
    localStorage.setItem(this.storageKey, lang);
  }

  private resolveInitialLang(): SupportedLanguage {
    const stored = this.readStoredLang();
    if (stored && this.isSupported(stored)) {
      return stored;
    }
    const current = this.translateService.currentLang ?? this.translateService.defaultLang;
    return this.isSupported(current) ? current : 'gl';
  }

  private readStoredLang(): string | null {
    try {
      return localStorage.getItem(this.storageKey);
    } catch {
      return null;
    }
  }

  private isSupported(lang: string | null): lang is SupportedLanguage {
    return lang === 'gl' || lang === 'es';
  }
}
